/**
 * Карточка игрока: профиль, кошелёк, прогресс, ассистент, лента леджера,
 * выдача валюты для тестов и сброс до новичка.
 *
 * Про выдачу стоит сказать прямо, потому что это единственная кнопка
 * админки, меняющая деньги. Она идёт ровно тем же путём, что награда за
 * бой: `applyMovements` → строка в `wallet_ledger` с причиной
 * `admin_grant` и ключом идемпотентности `admin:<admin_id>:<timestamp>`,
 * в одной транзакции со строкой аудита. Прямого `UPDATE wallets` нет ни
 * здесь, ни на сервере — журнал защищён триггерами, а ночная сверка
 * (`pnpm ledger:check`) сравнивает баланс с суммой движений. Выдача,
 * сделанная в обход, всплыла бы этой же ночью красным.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { request } from '../api/client.js'
import type {
  Currency,
  LedgerResponse,
  PlayerCard as PlayerCardData,
  ResetResponse,
} from '../api/types.js'
import { AuditTrail } from '../components/AuditTrail.js'
import { DataTable, type Column } from '../components/DataTable.js'
import { PageHead } from '../components/Layout.js'
import { Card, Delta, Money, Notice, Stat, currencyLabel, formatDate, shortId } from '../components/ui.js'
import { navigate } from '../router.js'

type LedgerRow = LedgerResponse['rows'][number]

const CURRENCIES: Currency[] = ['gold', 'gems', 'energy']

/** Причины движений человеческим языком. Незнакомую печатаем как есть. */
const REASONS: Record<string, string> = {
  signup_bonus: 'signup bonus',
  battle_reward: 'battle reward',
  battle_cost: 'battle entry',
  battle_refund: 'battle refund',
  gift_purchase: 'gift',
  gacha_open: 'gacha',
  daily_claim: 'daily claim',
  admin_grant: 'admin grant',
  purchase: 'purchase',
  energy_refill: 'energy refill',
  energy_regen: '⚡ refill',
}

export function PlayerCardView({ id }: { id: string }): JSX.Element {
  const [card, setCard] = useState<PlayerCardData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [refresh, setRefresh] = useState(0)

  const load = useCallback(async (): Promise<void> => {
    try {
      setCard(await request<PlayerCardData>(`/admin/players/${encodeURIComponent(id)}`))
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }, [id])

  useEffect(() => {
    void load()
  }, [load, refresh])

  if (error !== null) {
    return (
      <>
        <PageHead title="Player" />
        <Notice tone="error">{error}</Notice>
      </>
    )
  }

  if (card === null) {
    return (
      <>
        <PageHead title="Player" />
        <Card>Loading…</Card>
      </>
    )
  }

  const { profile, wallet } = card

  return (
    <>
      <PageHead
        title={profile.email}
        subtitle={<span className="mono">{profile.id}</span>}
        actions={
          <button className="btn btn--sm" type="button" onClick={() => navigate({ name: 'players' })}>
            ← Back to the list
          </button>
        }
      />

      <div className="stack">
        <div className="grid grid--2">
          <Card title="Profile">
            <dl className="dl">
              <dt>Role</dt>
              <dd>{profile.role === 'admin' ? 'administrator' : 'player'}</dd>
              <dt>Status</dt>
              <dd>
                {profile.anonymizedAt !== null ? (
                  <span className="badge badge--danger">anonymized {formatDate(profile.anonymizedAt)}</span>
                ) : profile.banned ? (
                  <span className="badge badge--danger">banned</span>
                ) : (
                  'active'
                )}
              </dd>
              <dt>Age 18+</dt>
              <dd>
                {profile.ageConfirmedAt === null ? (
                  <span className="badge badge--warn">not confirmed</span>
                ) : (
                  formatDate(profile.ageConfirmedAt)
                )}
              </dd>
              <dt>Level</dt>
              <dd>
                {profile.accountLevel} · {profile.accountXp} XP
              </dd>
              <dt>VIP</dt>
              <dd>{profile.vipPoints}</dd>
              <dt>Locale / geo</dt>
              <dd>
                {profile.locale} / {profile.geo ?? '—'}
              </dd>
              <dt>Signed up</dt>
              <dd>{formatDate(profile.createdAt)}</dd>
            </dl>
          </Card>

          <Card title="Wallet" hint="Energy is recomputed as of right now, exactly as the player sees it.">
            {wallet === null ? (
              <Notice tone="error">
This player has no wallet. That is broken data: a wallet is created together with the account.
              </Notice>
            ) : (
              <div className="stats">
                <Stat label="🪙 gold">
                  <Money currency="gold" value={wallet.gold} />
                </Stat>
                <Stat label="💎 gems">
                  <Money currency="gems" value={wallet.gems} />
                </Stat>
                <Stat label="⚡ energy">
                  <Money currency="energy" value={wallet.energy} />
                  <span className="muted" style={{ fontSize: 13, fontWeight: 400 }}>
                    {' '}
                    / {wallet.energyCap}
                  </span>
                </Stat>
              </div>
            )}
          </Card>
        </div>

        <GrantForm playerId={profile.id} onDone={() => setRefresh((n) => n + 1)} />

        <ResetBlock profile={profile} onDone={() => setRefresh((n) => n + 1)} />

        <AnonymizeBlock profile={profile} onDone={() => setRefresh((n) => n + 1)} />

        <LedgerFeed playerId={profile.id} refreshKey={refresh} />

        <div className="grid grid--2">
          <Card title="Assistant per chapter" padded={false}>
            <DataTable
              columns={[
                { key: 'chapter', title: 'Chapter', render: (row) => row.chapterTitle },
                {
                  key: 'heroine',
                  title: 'Heroine',
                  render: (row) => (
                    <span>
                      {row.heroineName} <span className="muted">· {row.archetype}</span>
                    </span>
                  ),
                },
                {
                  key: 'affinity',
                  title: '💜',
                  numeric: true,
                  render: (row) => (row.affinity === null ? '—' : row.affinity),
                },
                { key: 'chosen', title: 'Chosen', render: (row) => formatDate(row.chosenAt) },
              ]}
              rows={card.assistants}
              rowKey={(row) => row.chapterId}
              empty="No assistant chosen yet"
            />
          </Card>

          <Card title="Heroines in inventory" padded={false}>
            <DataTable
              columns={[
                { key: 'name', title: 'Heroine', render: (row) => row.name },
                { key: 'level', title: 'Lv.', numeric: true, render: (row) => row.level },
                { key: 'copies', title: 'Copies', numeric: true, render: (row) => row.copies },
                { key: 'affinity', title: '💜', numeric: true, render: (row) => row.affinity },
              ]}
              rows={card.heroines}
              rowKey={(row) => row.heroineId}
              empty="Inventory is empty"
            />
          </Card>
        </div>

        <Card title="Campaign progress" padded={false}>
          <DataTable
            columns={[
              { key: 'chapter', title: 'Chapter', render: (row) => row.chapterTitle },
              {
                key: 'level',
                title: 'Level',
                render: (row) => (
                  <span>
                    {row.ord}. {row.levelTitle} <span className="mono muted">{row.levelId}</span>
                  </span>
                ),
              },
              { key: 'stars', title: 'Stars', render: (row) => '★'.repeat(row.stars) },
              { key: 'cleared', title: 'Cleared', render: (row) => formatDate(row.clearedAt) },
            ]}
            rows={card.progress}
            rowKey={(row) => row.levelId}
            empty="Not a single level cleared"
          />
        </Card>

        <Card
          title="Administrator action log"
          hint="Every currency grant to this player leaves a row here."
          padded={false}
        >
          <AuditTrail entity="wallets" entityId={profile.id} refreshKey={refresh} />
        </Card>

        {/*
          Второй журнал, а не строки в первом: аудит ключуется таблицей,
          а сброс и обезличивание меняют `users`, а не `wallets`. До этой
          карточки оба действия писали след, которого не было видно ни на
          одном экране, — то есть требование «каждое изменение оставляет
          след» проверялось только запросом в базу.
        */}
        <Card
          title="Account action log"
          hint="Progress resets and anonymization land here."
          padded={false}
        >
          <AuditTrail entity="users" entityId={profile.id} refreshKey={refresh} />
        </Card>
      </div>
    </>
  )
}

/* ──────────────────────────── выдача валюты ────────────────────────────── */

function GrantForm({ playerId, onDone }: { playerId: string; onDone: () => void }): JSX.Element {
  const [currency, setCurrency] = useState<Currency>('gold')
  const [amount, setAmount] = useState('100')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)

  const parsed = Number(amount.trim())
  const valid = amount.trim() !== '' && Number.isInteger(parsed) && parsed !== 0

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault()
    setBusy(true)
    setError(null)
    setDone(null)

    try {
      const result = await request<{ idempotencyKey: string }>(
        `/admin/players/${encodeURIComponent(playerId)}/grant`,
        { method: 'POST', body: { currency, amount: parsed, note: note.trim() || undefined } },
      )
      setDone(result.idempotencyKey)
      setNote('')
      onDone()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card
      title="Grant currency (for testing)"
      hint="Through the ledger, reason admin_grant. A negative amount is a withdrawal: a mistaken grant is corrected the same way."
    >
      <form className="form-row" onSubmit={(e) => void submit(e)}>
        <label className="field" style={{ marginBottom: 0, minWidth: 170 }}>
          <span className="field__label">Currency</span>
          <select
            className="select"
            value={currency}
            onChange={(e) => setCurrency(e.target.value as Currency)}
            disabled={busy}
          >
            {CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {currencyLabel(c)}
              </option>
            ))}
          </select>
        </label>

        <label className="field" style={{ marginBottom: 0, minWidth: 150 }}>
          <span className="field__label">Amount</span>
          <input
            className={`input${amount.trim() !== '' && !valid ? ' input--invalid' : ''}`}
            inputMode="numeric"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            disabled={busy}
          />
        </label>

        <label className="field" style={{ marginBottom: 0, flex: '1 1 220px' }}>
          <span className="field__label">Note (goes to the audit)</span>
          <input
            className="input"
            value={note}
            placeholder="why you granted it"
            onChange={(e) => setNote(e.target.value)}
            disabled={busy}
          />
        </label>

        <button className="btn btn--primary" type="submit" disabled={busy || !valid}>
          {busy ? 'Posting…' : 'Grant'}
        </button>
      </form>

      {error !== null && (
        <div style={{ marginTop: 'var(--gap)' }}>
          <Notice tone="error">{error}</Notice>
        </div>
      )}
      {done !== null && (
        <div style={{ marginTop: 'var(--gap)' }}>
          <Notice tone="ok">
            Movement recorded. Idempotency key: <span className="mono">{done}</span>
          </Notice>
        </div>
      )}
    </Card>
  )
}

/* ───────────────────────── сброс до новичка ────────────────────────────── */

/**
 * «Пусть начнёт сначала».
 *
 * Кнопка существует затем, чтобы правильный способ сброса был единственным:
 * то же самое руками — это `DELETE FROM user_progress` и `UPDATE wallets`
 * в обход журнала, то есть прямое нарушение правила экономики. Здесь баланс
 * приводится к стартовому ДВИЖЕНИЕМ, и ночная сверка продолжает сходиться.
 *
 * Три галочки отделены от остального не для симметрии: прогресс ничего не
 * стоит и восстанавливается игрой заново, а за кошельком, инвентарём гачи
 * и роликами ассистентки стоят деньги — свои и партнёрские. Поэтому по
 * умолчанию они выключены, у каждой написано, что именно потеряется,
 * а подтверждение — вводом слова: диалог с одной кнопкой закрывают не глядя.
 */
function ResetBlock({
  profile,
  onDone,
}: {
  profile: PlayerCardData['profile']
  onDone: () => void
}): JSX.Element {
  const [wallet, setWallet] = useState(false)
  const [heroines, setHeroines] = useState(false)
  const [assistant, setAssistant] = useState(false)
  const [note, setNote] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<ResetResponse | null>(null)

  if (profile.anonymizedAt !== null) {
    return (
      <Card title="Reset to newbie">
        <Notice tone="warn">
          This account is anonymized: nobody can sign in, so there is nothing to start over.
        </Notice>
      </Card>
    )
  }

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault()
    setBusy(true)
    setError(null)
    setDone(null)

    try {
      const result = await request<ResetResponse>(
        `/admin/players/${encodeURIComponent(profile.id)}/reset`,
        { method: 'POST', body: { wallet, heroines, assistant, note: note.trim() || undefined } },
      )
      setDone(result)
      setConfirm('')
      setNote('')
      onDone()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card
      title="Reset to newbie"
      hint="The player keeps signing in with the same account and sees the game from the very beginning."
    >
      <form className="form" onSubmit={(e) => void submit(e)}>
        <p className="muted">
          Always wiped: cleared levels, unlocked scenes, tutorials, the chat history, daily claims,
          the custom deck, affinity, the account level (back to 1), battle history (including an
          unfinished battle) and the cheat log. The ledger, payments and generation jobs stay — they
          are money, not progress.
        </p>

        <label className="check">
          <input
            type="checkbox"
            checked={wallet}
            onChange={(e) => setWallet(e.target.checked)}
            disabled={busy}
          />
          <span>
            <b>Reset the wallet</b> to the starting balance.{' '}
            <span className="muted">
              Posted through the ledger as <span className="mono">admin_grant</span>, so the nightly
              reconciliation still holds.
            </span>
          </span>
        </label>

        <label className="check">
          <input
            type="checkbox"
            checked={heroines}
            onChange={(e) => setHeroines(e.target.checked)}
            disabled={busy}
          />
          <span>
            <b>Clear the heroine inventory</b> (gacha results).{' '}
            <span className="muted">
              Paid for, and the ledger tracks currency only — there is nothing to restore them from.
            </span>
          </span>
        </label>

        <label className="check">
          <input
            type="checkbox"
            checked={assistant}
            onChange={(e) => setAssistant(e.target.checked)}
            disabled={busy}
          />
          <span>
            <b>Delete the assistant</b> together with her photo and every clip shot with her face.{' '}
            <span className="muted">
              Those clips cost partner credits. Without this the player keeps the assistant they
              already made — one per account, so the creation screen would answer “you already have
              one”.
            </span>
          </span>
        </label>

        <label className="field">
          <span className="field__label">Note (goes to the audit)</span>
          <input
            className="input"
            value={note}
            placeholder="why you reset it"
            onChange={(e) => setNote(e.target.value)}
            disabled={busy}
          />
        </label>

        <p className="muted">
          There is no undo. Type <b>reset</b> to confirm.
        </p>

        <div className="row">
          <input
            className="input"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="reset"
            aria-label="Confirmation"
            disabled={busy}
          />
          <button className="btn btn--danger" type="submit" disabled={busy || confirm.trim() !== 'reset'}>
            {busy ? 'Resetting…' : 'Reset to newbie'}
          </button>
        </div>

        {error !== null && <Notice tone="error">{error}</Notice>}
        {done !== null && <ResetReport result={done} />}
      </form>
    </Card>
  )
}

/** Отчёт о сбросе. Цифрами, потому что «сделано» без них непроверяемо. */
function ResetReport({ result }: { result: ResetResponse }): JSX.Element {
  const { counts } = result
  const skipped = result.assistant?.skipped ?? []

  return (
    <>
      <Notice tone="ok">
        Done. Levels cleared: {counts.levels}, scenes: {counts.scenes}, tutorials: {counts.tutorials},
        chat messages: {counts.chatMessages}, daily claims: {counts.dailyClaims}, heroine rows touched:{' '}
        {counts.heroines}
        {counts.deck ? ', custom deck removed' : ''}, battles removed: {counts.battles}, cheat log entries removed: {counts.cheatAttempts}.
        {result.wallet !== null && (
          <>
            {' '}
            Wallet is back to 🪙 {result.wallet.gold} / 💎 {result.wallet.gems} / ⚡{' '}
            {result.wallet.energy} — through the ledger, key{' '}
            <span className="mono">{result.walletIdempotencyKey}</span>.
          </>
        )}
        {result.assistant !== null && (
          <>
            {' '}
            Assistant <span className="mono">{result.assistant.heroineId}</span> deleted with{' '}
            {result.assistant.clips} clip(s) and {result.assistant.photos} photo(s).
          </>
        )}
      </Notice>

      {skipped.length > 0 && (
        <Notice tone="warn">
          These files could not be removed from the zone and are orphans now — clear them by hand in
          the bunny panel:
          <ul>
            {skipped.map((s) => (
              <li key={s.path}>
                <span className="mono">{s.path}</span> — {s.reason}
              </li>
            ))}
          </ul>
        </Notice>
      )}
    </>
  )
}

/* ───────────────────────────── лента леджера ───────────────────────────── */

function LedgerFeed({ playerId, refreshKey }: { playerId: string; refreshKey: number }): JSX.Element {
  const [filter, setFilter] = useState<Currency | 'all'>('all')
  const [data, setData] = useState<LedgerResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)

    request<LedgerResponse>(`/admin/players/${encodeURIComponent(playerId)}/ledger`, {
      query: { currency: filter === 'all' ? undefined : filter, limit: 50 },
    })
      .then((result) => {
        if (cancelled) return
        setData(result)
        setError(null)
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [playerId, filter, refreshKey])

  const columns: Column<LedgerRow>[] = useMemo(
    () => [
      { key: 'when', title: 'When', render: (row) => formatDate(row.createdAt) },
      {
        key: 'currency',
        title: 'Currency',
        render: (row) => <span className={`cur-${row.currency}`}>{currencyLabel(row.currency)}</span>,
      },
      { key: 'delta', title: 'Movement', numeric: true, render: (row) => <Delta value={row.delta} /> },
      { key: 'reason', title: 'Reason', render: (row) => REASONS[row.reason] ?? row.reason },
      {
        key: 'ref',
        title: 'Reference',
        render: (row) => <span className="mono muted">{shortId(row.refId, 12)}</span>,
      },
      {
        key: 'key',
        title: 'Idempotency key',
        render: (row) => <span className="mono muted">{row.idempotencyKey ?? '—'}</span>,
      },
    ],
    [],
  )

  const totals = data?.totals

  return (
    <Card
      title="Ledger"
      hint="The truth about a balance is the sum of these rows. The log is append-only: it is never edited or deleted from."
      padded={false}
      actions={
        <div className="btn-group">
          <button className={filter === 'all' ? 'is-active' : undefined} onClick={() => setFilter('all')}>
            all
          </button>
          {CURRENCIES.map((c) => (
            <button
              key={c}
              className={filter === c ? 'is-active' : undefined}
              onClick={() => setFilter(c)}
            >
              {currencyLabel(c)}
            </button>
          ))}
        </div>
      }
    >
      {totals !== undefined && (
        <div className="row" style={{ padding: 'var(--gap) var(--gap-lg)', gap: 'var(--gap-lg)' }}>
          <span className="muted">Sum of movements:</span>
          <span>
            🪙 <Money currency="gold" value={totals.gold} />
          </span>
          <span>
            💎 <Money currency="gems" value={totals.gems} />
          </span>
          <span>
            ⚡ <Money currency="energy" value={totals.energy} />
          </span>
        </div>
      )}

      <DataTable
        columns={columns}
        rows={data?.rows ?? []}
        rowKey={(row) => String(row.id)}
        loading={loading}
        error={error}
        empty="No movements"
      />
    </Card>
  )
}

/* ─────────────────────── обезличивание («удаление») ────────────────────── */

/**
 * «Удалить аккаунт» по просьбе игрока (E11).
 *
 * Кнопка называет вещи своими именами: это НЕ удаление строки. Физически
 * удалить игрока нельзя — каскад снёс бы его `wallet_ledger`, а журнал
 * защищён от DELETE, и это не препятствие, а формулировка правила №1:
 * баланс доказуем ровно потому, что журнал только дописывается.
 *
 * Поэтому здесь стирается личность (почта, пароль, сессии, гео), а движения
 * валюты остаются как финансовая запись без человека. Ответ сервера
 * показывает, сколько строк журнала уцелело, и это число выводится на экран
 * намеренно: «удалил игрока» не должно читаться как «удалил и его деньги».
 *
 * Подтверждение — вводом слова, а не «вы уверены?»: действие необратимо,
 * а диалог с одной кнопкой закрывают не глядя.
 */
function AnonymizeBlock({
  profile,
  onDone,
}: {
  profile: PlayerCardData['profile']
  onDone: () => void
}): JSX.Element {
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<{ email: string; ledgerRows: number; revokedSessions: number } | null>(null)

  if (profile.anonymizedAt !== null) {
    return (
      <Card title="Account anonymized" hint="There is no identity left in this row. The action is irreversible.">
        <Notice tone="ok">
          Anonymized {formatDate(profile.anonymizedAt)}. The email was replaced with <span className="mono">{profile.email}</span>,
          sessions were wiped, the account cannot be signed into. Currency movements stayed in the log — and they must stay.
        </Notice>
      </Card>
    )
  }

  if (profile.role === 'admin') {
    return (
      <Card title="Account deletion">
        <Notice tone="warn">
This is an administrator. They cannot be anonymized: it would leave them without access to the panel. Remove the role first.
        </Notice>
      </Card>
    )
  }

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault()
    setBusy(true)
    setError(null)

    try {
      const result = await request<{ email: string; ledger_rows: number; revoked_sessions: number }>(
        `/admin/players/${encodeURIComponent(profile.id)}/anonymize`,
        { method: 'POST' },
      )
      setDone({
        email: result.email,
        ledgerRows: result.ledger_rows,
        revokedSessions: result.revoked_sessions,
      })
      setConfirm('')
      onDone()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card
      title="Account deletion (anonymization)"
      hint="Email, password and sessions are wiped. Currency movements remain: the log is append-only."
    >
      <form className="form" onSubmit={(e) => void submit(e)}>
        <p className="muted">
          After this the player cannot sign in, and their address becomes{' '}
          <span className="mono">deleted+…@deleted.invalid</span>. There is no undo. Type{' '}
          <b>anonymize</b> to confirm.
        </p>

        <div className="row">
          <input
            className="input"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="anonymize"
            aria-label="Confirmation"
            disabled={busy}
          />
          <button className="btn btn--danger" type="submit" disabled={busy || confirm.trim() !== 'anonymize'}>
            {busy ? 'Anonymizing…' : 'Anonymize'}
          </button>
        </div>

        {error !== null && <Notice tone="error">{error}</Notice>}
        {done !== null && (
          <Notice tone="ok">
            Done. New address <span className="mono">{done.email}</span>, sessions revoked:{' '}
            {done.revokedSessions}. {done.ledgerRows} movements are still in the log — the economy still
            reconciles.
          </Notice>
        )}
      </form>
    </Card>
  )
}
